import { Controller } from "@hotwired/stimulus";

// Displays ActiveStorage direct upload progress for a file input.
//
// Usage:
// - Wrap an <input type="file" direct_upload> in an element with:
//   data-controller="direct-upload"
// - Mark the input as:
//   data-direct-upload-target="input"
// - Provide a container for progress items:
//   data-direct-upload-target="list"
//
// This controller listens for ActiveStorage events:
// - direct-upload:initialize
// - direct-upload:start
// - direct-upload:progress
// - direct-upload:error
// - direct-upload:end
export default class extends Controller {
  static targets = ["input", "list"];

  connect() {
    if (!this.hasInputTarget) return;

    this.itemsById = new Map();

    this.onInitialize = this.onInitialize.bind(this);
    this.onStart = this.onStart.bind(this);
    this.onProgress = this.onProgress.bind(this);
    this.onError = this.onError.bind(this);
    this.onEnd = this.onEnd.bind(this);

    this.inputTarget.addEventListener("direct-upload:initialize", this.onInitialize);
    this.inputTarget.addEventListener("direct-upload:start", this.onStart);
    this.inputTarget.addEventListener("direct-upload:progress", this.onProgress);
    this.inputTarget.addEventListener("direct-upload:error", this.onError);
    this.inputTarget.addEventListener("direct-upload:end", this.onEnd);
  }

  disconnect() {
    if (!this.hasInputTarget) return;
    this.inputTarget.removeEventListener("direct-upload:initialize", this.onInitialize);
    this.inputTarget.removeEventListener("direct-upload:start", this.onStart);
    this.inputTarget.removeEventListener("direct-upload:progress", this.onProgress);
    this.inputTarget.removeEventListener("direct-upload:error", this.onError);
    this.inputTarget.removeEventListener("direct-upload:end", this.onEnd);
  }

  onInitialize(event) {
    const { id, file } = event.detail || {};
    if (!id) return;

    // Reset when a new selection starts.
    if (this.itemsById.size === 0) this.clearList();

    const item = this.buildItem({ id, file });
    this.itemsById.set(id, item);
    this.listTarget?.appendChild(item.root);
  }

  onStart(event) {
    const { id } = event.detail || {};
    const item = this.itemsById.get(id);
    if (!item) return;
    item.status.textContent = "Uploading…";
    item.bar.classList.add("progress-bar-striped", "progress-bar-animated");
  }

  onProgress(event) {
    const { id, progress } = event.detail || {};
    const item = this.itemsById.get(id);
    if (!item) return;

    const pct = Math.max(0, Math.min(100, Number(progress) || 0));
    item.bar.style.width = `${pct}%`;
    item.bar.setAttribute("aria-valuenow", `${pct}`);
    item.status.textContent = `${pct}%`;
  }

  onError(event) {
    const { id, error } = event.detail || {};
    const item = this.itemsById.get(id);
    if (!item) return;

    item.root.classList.add("text-danger");
    item.bar.classList.remove("progress-bar-animated");
    item.bar.classList.add("bg-danger");
    item.status.textContent = error?.toString?.() || "Upload failed";
  }

  onEnd(event) {
    const { id } = event.detail || {};
    const item = this.itemsById.get(id);
    if (!item) return;

    item.bar.classList.remove("progress-bar-animated");
    item.bar.classList.remove("progress-bar-striped");
    item.bar.classList.add("bg-success");
    item.bar.style.width = "100%";
    item.bar.setAttribute("aria-valuenow", "100");
    item.status.textContent = "Done";
  }

  clearList() {
    if (this.hasListTarget) this.listTarget.innerHTML = "";
  }

  buildItem({ file }) {
    const root = document.createElement("div");
    root.className = "mt-2";

    const header = document.createElement("div");
    header.className = "d-flex align-items-center justify-content-between gap-2 small";

    const name = document.createElement("div");
    name.className = "text-truncate";
    name.textContent = file?.name || "Upload";

    const status = document.createElement("div");
    status.className = "text-body-secondary";
    status.textContent = "Queued";

    header.appendChild(name);
    header.appendChild(status);

    const progress = document.createElement("div");
    progress.className = "progress mt-1";
    progress.style.height = "6px";

    const bar = document.createElement("div");
    bar.className = "progress-bar";
    bar.setAttribute("role", "progressbar");
    bar.setAttribute("aria-valuemin", "0");
    bar.setAttribute("aria-valuemax", "100");
    bar.setAttribute("aria-valuenow", "0");
    bar.style.width = "0%";

    progress.appendChild(bar);

    root.appendChild(header);
    root.appendChild(progress);

    return { root, bar, status };
  }
}
